import {addQueryToPath} from '../../../libs/shared/helpers/src/browser/urlHelpers';
import {isTeamNamespace} from '../../helpers/models/namespaceHelpers';
import {RuleComplexRecipeTypesEnum} from '../../settings/rules/ruleRecipeModel';
import {IntegrationTypeCategoriesEnum} from '../api/integrationTypesModel';
import {
  SettingsAnswersUrl,
  SettingsApplicationComponentsEnum,
  SettingsApplicationsDetailsUrl,
  SettingsApplicationsUrl,
  SettingsAppsAppDetailsTabsEnum,
  SettingsAppsTabsEnum,
  SettingsAutolinkersUrl,
  SettingsDevelopersPagesEnum,
  SettingsDevelopersUrl,
  SettingsInboxesSubPagesEnum,
  SettingsInboxesUrl,
  SettingsNotificationsUrl,
  SettingsOauthClientUrl,
  SettingsPreferencesUrl,
  SettingsPreviewersUrl,
  SettingsResourcePagesEnum,
  SettingsRootPagesEnum,
  SettingsRulesUrl,
  SettingsServersUrl,
  SettingsSignaturesUrl,
  SettingsSnoozesUrl,
  SettingsTabsPagesEnum,
  SettingsTagsUrl,
  SettingsTeammateUrl,
  SettingsUrlModel,
  SettingsVacationRespondersUrl,
} from './settingsUrlModel';

export function buildSettingsPath(url: SettingsUrlModel): string {
  switch (url.rootPage) {
    case SettingsRootPagesEnum.TAGS: {
      return addQueryToPath(buildSettingsTagsPath(url), {
        page: url.tabPage || undefined,
      });
    }
    case SettingsRootPagesEnum.SIGNATURES: {
      return addQueryToPath(buildSettingsSignaturesPath(url));
    }

    case SettingsRootPagesEnum.ANSWERS: {
      return buildSettingsAnswersPath(url);
    }

    case SettingsRootPagesEnum.RULES: {
      return buildSettingsRulesPath(url);
    }

    case SettingsRootPagesEnum.INBOXES: {
      return buildSettingsInboxesPath(url);
    }

    case SettingsRootPagesEnum.TEAMMATES: {
      return buildSettingsTeammatesPath(url);
    }

    case SettingsRootPagesEnum.USER_PREFERENCES: {
      return addQueryToPath(buildSettingsPreferencesPath(url), {
        search: url.initialPreference || undefined,
      });
    }

    case SettingsRootPagesEnum.APPLICATIONS: {
      return buildSettingsApplicationsPathAndQuery(url);
    }

    case SettingsRootPagesEnum.SNOOZES: {
      return buildSettingsSnoozePath(url);
    }

    case SettingsRootPagesEnum.VACATION_RESPONDERS: {
      return buildSettingsVacationRespondersPath(url);
    }

    case SettingsRootPagesEnum.NOTIFICATIONS: {
      return buildSettingsNotificationsPath(url);
    }

    default:
      throw new Error(`Path builder not implemented for ${url.rootPage}`);
  }
}

function buildSettingsTagsPath(url: SettingsTagsUrl): string {
  const pageSuffix = url.resourcePage ? `/${url.resourcePage}` : '';

  if (url.tagId) {
    return `/settings/${url.namespace}/${url.rootPage}/${url.tagId}${pageSuffix}`;
  }

  return `/settings/${url.namespace}/${url.rootPage}${pageSuffix}`;
}

function buildSettingsAnswersPath(url: SettingsAnswersUrl): string {
  const prefix = `/settings/${url.namespace}/${url.rootPage}`;
  const pageSuffix = url.resourcePage ? url.resourcePage : '';

  if (url.inFolder) {
    return url.folderId
      ? `${prefix}/folders/${url.folderId}/${pageSuffix}/`
      : `${prefix}/folders/${pageSuffix}`;
  }

  if (url.answerId && url.resourcePage === SettingsResourcePagesEnum.EDIT) {
    return `${prefix}/${pageSuffix}/${url.answerId}/overview`;
  }

  return `${prefix}/${pageSuffix}`;
}

function buildSettingsSignaturesPath(url: SettingsSignaturesUrl): string {
  const pageSuffix = url.resourcePage ? `/${url.resourcePage}` : '';

  if (url.signatureId) {
    return `/settings/${url.namespace}/${url.rootPage}${pageSuffix}/${url.signatureId}`;
  }

  return `/settings/${url.namespace}/${url.rootPage}${pageSuffix}`;
}

function buildSettingsRulesPath(url: SettingsRulesUrl): string {
  const baseRoute = `/settings/${url.namespace}/${url.rootPage}`;

  if (url.resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    return addQueryToPath(baseRoute, {
      only_smart: url.filters?.onlySmart,
      categories: url.filters?.categories?.join(','),
      query: url.filters?.query,
      inbox: url.filters?.inbox,
    });
  }

  if (url.resourcePage === SettingsResourcePagesEnum.NEW) {
    if (isTeamNamespace(url.namespace)) {
      return `${baseRoute}/library`;
    }
    return `${baseRoute}/${url.resourcePage}`;
  }

  if (url.resourcePage === SettingsResourcePagesEnum.EDIT) {
    switch (url.recipe) {
      case RuleComplexRecipeTypesEnum.SLA:
        return `${baseRoute}/recipes/${url.bundleId}`;
      case RuleComplexRecipeTypesEnum.CSAT:
        return `${baseRoute}/recipes/csat/${url.bundleId}`;
      case RuleComplexRecipeTypesEnum.REQUIRED_TAGGING:
        return `${baseRoute}/recipes/required-tagging/${url.bundleId}`;
      default:
        return `${baseRoute}/${url.resourcePage}/${url.ruleId}`;
    }
  }

  if (url.resourcePage === SettingsResourcePagesEnum.RECIPES) {
    switch (url.recipe) {
      case RuleComplexRecipeTypesEnum.SLA:
        return `${baseRoute}/recipes/sla`;
      case RuleComplexRecipeTypesEnum.CSAT:
        return `${baseRoute}/recipes/csat/new`;
      case RuleComplexRecipeTypesEnum.REQUIRED_TAGGING:
        return `${baseRoute}/recipes/required-tagging`;
      default:
        return `${baseRoute}/new/${url.recipe}`;
    }
  }

  return `${baseRoute}/${url.resourcePage}`;
}

function buildSettingsInboxesPath(url: SettingsInboxesUrl): string {
  const baseRoute = url.subPage
    ? `/settings/${url.namespace}/${url.rootPage}/${url.resourcePage}/${url.inboxId}/${url.subPage}`
    : `/settings/${url.namespace}/${url.rootPage}`;

  if (url.subPage) {
    if (url.subPage === SettingsInboxesSubPagesEnum.CHANNELS) {
      return url.channelId ? `${baseRoute}/${url.channelId}` : `${baseRoute}/list`;
    }
    return baseRoute;
  }

  if (
    url.resourcePage === SettingsResourcePagesEnum.OVERVIEW ||
    url.resourcePage === SettingsResourcePagesEnum.EDIT
  ) {
    return baseRoute;
  }

  if (url.resourcePage === SettingsResourcePagesEnum.IMPORT_CUSTOM_ATTRIBUTES) {
    return `${baseRoute}/${url.resourcePage}`;
  }

  throw new Error('Not implemented');
}

function buildSettingsTeammatesPath(url: SettingsTeammateUrl): string {
  const baseRoute = `/settings/${url.namespace}/${url.rootPage}`;

  if (url.resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    return baseRoute;
  }

  if (url.resourcePage === SettingsResourcePagesEnum.IMPORT_CUSTOM_ATTRIBUTES) {
    return `${baseRoute}/${url.resourcePage}`;
  }

  throw new Error('Not implemented');
}

function buildSettingsPreferencesPath(url: SettingsPreferencesUrl): string {
  return `/settings/${url.namespace}/${url.rootPage}`;
}

function buildSettingsApplicationsPath(url: SettingsApplicationsUrl): string {
  switch (url.componentPage) {
    case SettingsApplicationComponentsEnum.LIST:
      return '/settings/applications';

    case SettingsApplicationComponentsEnum.AUTOLINKERS:
      return buildSettingsAutolinkersPath(url);

    case SettingsApplicationComponentsEnum.OAUTH_CLIENT:
      return buildSettingsOauthClientPath(url);

    case SettingsApplicationComponentsEnum.PREVIEWERS:
      return buildSettingsPreviewersPath(url);

    case SettingsApplicationComponentsEnum.SERVERS:
      return buildSettingsServersPath(url);

    case SettingsApplicationComponentsEnum.DETAILS:
    default:
      return buildSettingsApplicationsDetailsPath(url);
  }
}

function buildSettingsApplicationsPathAndQuery(url: SettingsApplicationsUrl): string {
  const {searchQuery} = url;

  const path = buildSettingsApplicationsPath(url);

  if (!searchQuery) {
    return path;
  }

  return addQueryToPath(path, {q: searchQuery});
}

function buildSettingsApplicationsDetailsPath(url: SettingsApplicationsDetailsUrl): string {
  const baseRoute = `/settings/applications`;

  if (url.resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    return `${baseRoute}/${url.applicationId}/details`;
  }

  if (url.resourcePage === SettingsResourcePagesEnum.EDIT) {
    return `${baseRoute}/${url.applicationId}/details/edit`;
  }

  if (url.resourcePage === SettingsResourcePagesEnum.NEW) {
    return `${baseRoute}/new`;
  }

  return `${baseRoute}/${url.resourcePage}`;
}

function buildSettingsAutolinkersPath(url: SettingsAutolinkersUrl): string {
  const baseRoute = `/settings/applications/${url.applicationId}/autolinkers`;

  if (url.resourcePage === SettingsResourcePagesEnum.OVERVIEW && !url.autolinkerId) {
    return baseRoute;
  }

  if (url.resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    return `${baseRoute}/${url.autolinkerId}/details`;
  }

  if (url.resourcePage === SettingsResourcePagesEnum.EDIT) {
    return `${baseRoute}/${url.autolinkerId}/${url.resourcePage}`;
  }

  return `${baseRoute}/${url.resourcePage}`;
}

function buildSettingsOauthClientPath(url: SettingsOauthClientUrl): string {
  const baseRoute = `/settings/applications/${url.applicationId}/oauth_client`;

  if (url.resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    return baseRoute;
  }

  return `${baseRoute}/${url.resourcePage}`;
}

function buildSettingsPreviewersPath(url: SettingsPreviewersUrl): string {
  const baseRoute = `/settings/applications/${url.applicationId}/previewers`;

  if (url.resourcePage === SettingsResourcePagesEnum.OVERVIEW && !url.previewerId) {
    return baseRoute;
  }

  if (url.resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    return `${baseRoute}/${url.previewerId}/details`;
  }

  if (url.resourcePage === SettingsResourcePagesEnum.EDIT) {
    return `${baseRoute}/${url.previewerId}/${url.resourcePage}`;
  }

  return `${baseRoute}/${url.resourcePage}`;
}

function buildSettingsServersPath(url: SettingsServersUrl): string {
  const baseRoute = `/settings/applications/${url.applicationId}/servers`;

  if (url.resourcePage === SettingsResourcePagesEnum.OVERVIEW && !url.serverId) {
    return baseRoute;
  }

  if (url.resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    return `${baseRoute}/${url.serverId}/details`;
  }

  if (url.resourcePage === SettingsResourcePagesEnum.EDIT) {
    return `${baseRoute}/${url.serverId}/${url.resourcePage}`;
  }

  return `${baseRoute}/${url.resourcePage}`;
}

function buildSettingsSnoozePath(url: SettingsSnoozesUrl): string {
  const pageSuffix = url.resourcePage ? `/${url.resourcePage}` : '';

  if (url.snoozeId) {
    return `/settings/${url.namespace}/${url.rootPage}${pageSuffix}/${url.snoozeId}`;
  }

  return `/settings/${url.namespace}/${url.rootPage}${pageSuffix}`;
}

function buildSettingsVacationRespondersPath(url: SettingsVacationRespondersUrl): string {
  const pageSuffix = url.resourcePage ? `/${url.resourcePage}` : '';

  if (url.vacationResponderId) {
    return `/settings/${url.namespace}/${url.rootPage}${pageSuffix}/${url.vacationResponderId}`;
  }

  return `/settings/${url.namespace}/${url.rootPage}${pageSuffix}`;
}

export function buildSettingsDevelopersPath(url: SettingsDevelopersUrl): string {
  const basePath = '/settings/developers';

  if (url.subPage === SettingsDevelopersPagesEnum.TOKENS) {
    return `${basePath}/tokens/${url.tokenId ? `${url.tokenId}/` : ''}`;
  }
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATION_FEATURES) {
    return `${basePath}/${url.applicationId}/features/${url.feature ? `${url.feature}/` : ''}`;
  }
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATION_INFO) {
    return `${basePath}/${url.applicationId}/details`;
  }
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATION_OAUTH) {
    return `${basePath}/${url.applicationId}/oauth/`;
  }
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATION_SERVERS) {
    const baseServersPath = `${basePath}/${url.applicationId}/servers`;

    if (url.serverId) {
      return `${baseServersPath}/${url.serverId}`;
    }
    if (url.isConnecting) {
      return `${baseServersPath}/connect`;
    }
    return baseServersPath;
  }
  return basePath;
}

/*
 * Billing.
 */

export function buildSettingsBillingUrl() {
  return '/settings/billing';
}

/*
 * Apps helper.
 */

export function buildSettingsAppsTabUrl(
  tab: SettingsAppsTabsEnum,
  searchQuery?: string,
  previousUrl?: string,
  source?: string,
) {
  const path = `/settings/apps/${tab}`;

  if (searchQuery || previousUrl || source) {
    return addQueryToPath(path, {q: searchQuery, prev: computePreviousUrl(previousUrl), source});
  }
  return path;
}

export function buildSettingsAppsAppDetailsUrl(
  appId: string,
  previousUrl?: string,
  detailsPage?: SettingsAppsAppDetailsTabsEnum,
  shouldOpenConfiguration?: boolean,
) {
  const path = `/settings/apps/${SettingsAppsTabsEnum.DETAILS}/${appId}/${
    detailsPage ?? SettingsAppsAppDetailsTabsEnum.OVERVIEW
  }`;

  if (previousUrl || shouldOpenConfiguration) {
    return addQueryToPath(path, {
      prev: computePreviousUrl(previousUrl),
      open_configuration: shouldOpenConfiguration,
    });
  }
  return path;
}

export function buildSettingsAppsCategoryUrl(category: IntegrationTypeCategoriesEnum, previousUrl?: string) {
  const path = `/settings/apps/${SettingsAppsTabsEnum.CATEGORIES}/${category}`;

  if (previousUrl) {
    return addQueryToPath(path, {prev: computePreviousUrl(previousUrl)});
  }
  return path;
}

function computePreviousUrl(previousUrl?: string) {
  if (!previousUrl) {
    return undefined;
  }
  return encodeURIComponent(previousUrl);
}

function buildSettingsNotificationsPath(url: SettingsNotificationsUrl): string {
  if (url.tabPage === SettingsTabsPagesEnum.ADVANCED) {
    if (url.resourcePage === SettingsResourcePagesEnum.NEW) {
      return `/settings/notifications/advanced/new`;
    }

    if (url.notificationIndex || url.resourcePage === SettingsResourcePagesEnum.EDIT) {
      return `/settings/notifications/advanced/edit/${url.notificationIndex}`;
    }

    return `/settings/notifications/advanced`;
  }

  return `/settings/notifications`;
}
